import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { apiClient } from "../lib/site";

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/case-studies/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <Section testId="case-not-found">
        <p className="overline mb-4">Not found</p>
        <h1 className="font-serif-display text-5xl text-white font-light mb-8">Case study not found.</h1>
        <Link to="/case-studies" className="btn-secondary">Back to all case studies</Link>
      </Section>
    );
  }
  if (!item) return <Section><p className="text-zinc-500">Loading…</p></Section>;

  return (
    <article data-testid="case-detail-page">
      <SeoHead
        title={`${item.client} — ${item.title}`}
        description={item.summary}
        path={`/case-studies/${item.slug}`}
        image={item.cover_image}
        type="article"
      />
      {/* Hero */}
      <section className="relative px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16 border-b border-white/5">
        <Link
          to="/case-studies"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition text-sm mb-12"
          data-testid="case-back-link"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> All case studies
        </Link>
        <motion.p {...fadeUp} className="overline mb-6">
          {item.industry} · {item.client} · {item.duration}
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="font-serif-display font-light text-white leading-[1.05] tracking-tight max-w-5xl"
          style={{ fontSize: "clamp(36px, 6vw, 80px)", letterSpacing: "-0.025em" }}
        >
          {item.title}
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 text-zinc-300 text-lg md:text-xl max-w-3xl leading-relaxed"
        >
          {item.summary}
        </motion.p>
      </section>

      {/* Cover image */}
      <section className="px-6 md:px-12 lg:px-16 py-16">
        <div className="aspect-[16/9] overflow-hidden bg-[#0A0A0C]">
          <img src={item.cover_image} alt="" className="w-full h-full object-cover opacity-90" />
        </div>
      </section>

      {/* Body */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <p className="overline mb-3">Services</p>
              <ul className="space-y-1 text-zinc-300 text-sm">
                {item.services.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="overline mb-3">Industry</p>
              <p className="text-zinc-300 text-sm">{item.industry}</p>
            </div>
            <div>
              <p className="overline mb-3">Duration</p>
              <p className="text-zinc-300 text-sm">{item.duration}</p>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-12">
            <div>
              <p className="overline mb-4">The challenge</p>
              <p className="text-zinc-300 text-lg leading-relaxed">{item.challenge}</p>
            </div>
            <div>
              <p className="overline mb-4">The solution</p>
              <p className="text-zinc-300 text-lg leading-relaxed">{item.solution}</p>
            </div>
            <div>
              <p className="overline mb-4">The impact</p>
              <ul className="space-y-3">
                {item.impact.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white text-base leading-relaxed">
                    <Check size={16} strokeWidth={1.5} style={{ color: "#C2533B" }} className="mt-1.5 shrink-0" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 px-6 md:px-12 lg:px-16 py-24 bg-[#0A0A0C]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(32px, 5vw, 56px)", letterSpacing: "-0.025em" }}
            >
              Have a platform like this on your roadmap?
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="case-cta">
              Start a Conversation <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
